import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  useTheme,
  Hidden,
  makeStyles,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { CustomTheme } from "../../../../../../types/customTheme";
import SwitchCommon from "../../../../../../components/switches/SwitchCommon";

const useStyles = makeStyles((theme: CustomTheme) => ({
  bannerContent: {
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      padding: theme.spacing(2),
    },
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(4),
    },
  },
  imageWrapper: {
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
      display: "flex",
      justifyContent: "center",
    },
  },
  imagePaper: {
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: theme.palette.background.menuProductColor,
    border: "1px solid rgba(255,255,255,0.1)",
    [theme.breakpoints.down("xs")]: {
      width: 120,
    },
    [theme.breakpoints.up("sm")]: {
      width: 160,
    },
  },
  productImage: {
    width: "100%",
    objectFit: "cover",
    [theme.breakpoints.down("xs")]: {
      height: 100,
    },
    [theme.breakpoints.up("sm")]: {
      height: 130,
    },
  },
  infoSection: {
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(4),
    },
  },
  idCards: {
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },
  },
  idCard: {
    backgroundColor: theme.palette.background.menuProductColor,
    borderRadius: 10,
    border: `1px solid ${theme.palette.background.entity_border}`,
    backdropFilter: "blur(4px)",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "auto",
      marginRight: 0,
    },
    [theme.breakpoints.up("sm")]: {
      minWidth: 120,
      marginRight: theme.spacing(2),
    },
  },
  changeCoverButton: {
    backgroundColor: theme.palette.background.menuProductColor,
    fontSize: "10px",
    textTransform: "none",
    borderRadius: 0,
    [theme.breakpoints.down("xs")]: {
      height: 30,
    },
  },
}));

interface ProductBannerProps {
  productData: any;
  productId: string;
  categoryId: number | null;
  isValidMainProduct: boolean;
  onValidToggle: (checked: boolean) => void;
  onCopyText: (text: string) => void;
  wrapperData: any;
  isEditDisabled: boolean;
}

const ProductBanner: React.FC<ProductBannerProps> = ({
  productData,
  productId,
  categoryId,
  isValidMainProduct,
  onValidToggle,
  onCopyText,
  wrapperData,
  isEditDisabled,
}) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();

  return (
    <Paper
      elevation={0}
      style={{
        borderRadius: 16,
        overflow: "hidden",
        backgroundColor: theme.palette.type === "dark" ? "#151d26" : "#F7F9FB",
        marginBottom: 24,
        position: "relative",
        minHeight: 300,
        display: "flex",
        alignItems: "flex-end",
        border: `1px solid ${
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.08)"
        }`,
      }}
    >
      {/* Background Image Layer */}
      <Box
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${wrapperData?.img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter:
            theme.palette.type === "dark"
              ? "brightness(0.35)"
              : "brightness(0.9)",
          zIndex: 1,
        }}
      />

      {/* Gradient Overlay */}
      <Box
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            theme.palette.type === "dark"
              ? "linear-gradient(90deg, rgba(21, 29, 38, 1) 0%, rgba(21, 29, 38, 0.8) 50%, rgba(21, 29, 38, 0.15) 100%)"
              : "linear-gradient(90deg, rgba(247, 249, 251, 1) 0%, rgba(247, 249, 251, 0.85) 50%, rgba(247, 249, 251, 0.2) 100%)",
          zIndex: 2,
        }}
      />

      {/* Content Layer */}
      <Box
        display="flex"
        width="100%"
        className={classes.bannerContent}
        style={{ zIndex: 3, position: "relative" }}
      >
        {/* Left Side: Change Cover Image Card */}
        <Box className={classes.imageWrapper}>
          <Paper elevation={4} className={classes.imagePaper}>
            <img
              src={wrapperData?.img}
              alt="Product"
              className={classes.productImage}
            />
            <Button
              fullWidth
              size="small"
              startIcon={<Icon icon="lucide:camera" width="14" />}
              className={classes.changeCoverButton}
            >
              Change Cover
            </Button>
          </Paper>
        </Box>

        {/* Right Side: Info and Badges */}
        <Box
          className={classes.infoSection}
          flexGrow={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {/* Desktop View */}
          <Hidden xsDown>
            <Box display="flex" alignItems="center" mb={1} flexWrap="wrap">
              <Typography
                variant="h4"
                style={{ fontWeight: "bold" }}
                align="left"
              >
                {wrapperData?.name}
              </Typography>
              <Box
                ml={2}
                px={1.5}
                py={0.3}
                style={{
                  backgroundColor: isValidMainProduct
                    ? theme.palette.custom.green.main
                    : theme.palette.custom.red.main,
                  borderRadius: 10,
                  border: "none",
                  marginRight: "8px",
                }}
              >
                <Typography
                  style={{
                    color: isValidMainProduct
                      ? theme.palette.custom.green.contrastText
                      : theme.palette.custom.red.contrastText,
                    fontSize: 12,
                    fontWeight: "bold",
                  }}
                >
                  {isValidMainProduct ? "Valid" : "Invalid"}
                </Typography>
              </Box>
              <FormControlLabel
                control={<SwitchCommon />}
                checked={isValidMainProduct}
                onChange={(e: any) => onValidToggle(e.target.checked)}
                label=""
                disabled={isEditDisabled}
              />
            </Box>
          </Hidden>

          {/* Mobile View */}
          <Hidden smUp>
            <Box mb={1}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography
                  variant="h5"
                  style={{ fontWeight: "bold" }}
                  align="left"
                >
                  {wrapperData?.name}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box
                    px={1.5}
                    py={0.3}
                    style={{
                      backgroundColor: isValidMainProduct
                        ? theme.palette.custom.green.main
                        : theme.palette.custom.red.main,
                      borderRadius: 10,
                      border: "none",
                      marginRight: "8px",
                    }}
                  >
                    <Typography
                      style={{
                        color: isValidMainProduct
                          ? theme.palette.custom.green.contrastText
                          : theme.palette.custom.red.contrastText,
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    >
                      {isValidMainProduct ? "Valid" : "Invalid"}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={<SwitchCommon />}
                    checked={isValidMainProduct}
                    onChange={(e: any) => onValidToggle(e.target.checked)}
                    label=""
                  />
                </Box>
              </Box>
            </Box>
          </Hidden>

          <Typography variant="subtitle2" align="left">
            {wrapperData?.name}
          </Typography>

          <Typography
            variant="body1"
            align="left"
            style={{
              maxWidth: "500px",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            {wrapperData?.desc ||
              "Delicious product made with premium ingredients fresh every day."}
          </Typography>

          {/* ID Cards Section */}
          <Box display="flex" className={classes.idCards}>
            {/* ID Card */}
            <Box p={1.5} className={classes.idCard}>
              <Typography
                variant="caption"
                style={{ display: "block" }}
                align="left"
              >
                ID
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={0.5}
              >
                <Typography style={{ fontWeight: 600 }}>{productId}</Typography>
                <IconButton
                  size="small"
                  style={{ padding: 4 }}
                  onClick={() => onCopyText(productId)}
                >
                  <Icon icon="lucide:copy" width="14" />
                </IconButton>
              </Box>
            </Box>

            {/* Category ID Card */}
            <Box p={1.5} className={classes.idCard}>
              <Typography
                variant="caption"
                style={{ display: "block" }}
                align="left"
              >
                Category ID
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={0.5}
              >
                <Typography style={{ fontWeight: 600 }}>
                  {categoryId}
                </Typography>
                <IconButton
                  size="small"
                  style={{ padding: 4 }}
                  onClick={() => onCopyText(categoryId?.toString() || "")}
                >
                  <Icon icon="lucide:copy" width="14" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProductBanner;
